import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { fmtInr, portfolioKpis } from './portfolio.utils';

const cardIn = keyframes`from { opacity: 0; transform: translateY(14px) scale(.97); } to { opacity: 1; transform: none; }`;

// Line icons drawn faintly in each card's corner
const ICONS = {
    active: <path d="M4 7.5a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />,
    onSchedule: <><circle cx="12" cy="12" r="8" /><path d="M8.5 12.2l2.4 2.4 4.6-4.8" /></>,
    delayed: <><circle cx="12" cy="12" r="8" /><path d="M12 7.5V12l3 2" /></>,
    completed: <path d="M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M9 20h6" />,
};

// Tint, border, hover shadow and icon colour per card (the v69 design's final palette)
const TONES = {
    active: { t: '#eef0fd', t2: '#fafbff', b: '#e0e3f8', s: 'rgba(79,70,229,.10)', w: '#d3d7f5' },
    onSchedule: { t: '#fdf7e1', t2: '#fffdf6', b: '#f3e9c4', s: 'rgba(202,138,4,.10)', w: '#efe0ad' },
    delayed: { t: '#fdeee8', t2: '#fffaf7', b: '#f6dcd2', s: 'rgba(234,88,60,.10)', w: '#f2cbbd' },
    completed: { t: '#e9f7f0', t2: '#f8fdfa', b: '#d4ede1', s: 'rgba(16,150,100,.10)', w: '#c3e5d4' },
};

function Card({ tone, index, label, value, sub }) {
    const c = TONES[tone];
    return (
        <Box sx={{
            position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0,
            p: { xs: '16px 18px', md: '20px 22px' }, borderRadius: '14px', border: `1px solid ${c.b}`,
            background: `linear-gradient(150deg, ${c.t} 0%, ${c.t2} 100%)`,
            boxShadow: `0 2px 4px rgba(15,23,42,.04), 0 12px 28px ${c.s}`,
            animation: `${cardIn} .5s cubic-bezier(.2,.8,.2,1) both`, animationDelay: `${index * 0.07}s`,
            transition: 'transform .2s, box-shadow .2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 4px 8px rgba(15,23,42,.05), 0 20px 40px ${c.s}` },
            '&:hover .kpi-icon': { transform: 'translateY(-50%) scale(1.08) rotate(-4deg)' },
        }}>
            <Box component="svg" className="kpi-icon" viewBox="0 0 24 24" sx={{
                position: 'absolute', right: '-6px', top: '50%', transform: 'translateY(-50%)', width: 96, height: 96,
                fill: 'none', stroke: c.w, strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round', transition: 'transform .3s',
            }}>{ICONS[tone]}</Box>
            <Typography sx={{ position: 'relative', fontSize: '12px', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#475569' }}>{label}</Typography>
            <Typography sx={{ position: 'relative', fontSize: { xs: '30px', md: '36px' }, fontWeight: 800, letterSpacing: '-.02em', color: '#0f172a', lineHeight: 1.15 }}>{value}</Typography>
            <Typography noWrap title={sub} sx={{ position: 'relative', fontSize: '13px', color: '#526072' }}>{sub}</Typography>
        </Box>
    );
}

// Four soft-tinted cards for the projects in view. Budget figures appear only when the server sent them (admins).
export default function PortfolioKpis({ projects }) {
    const k = portfolioKpis(projects);
    const cities = `${k.cities} cit${k.cities === 1 ? 'y' : 'ies'}`;
    return (
        <Box sx={{ display: 'grid', gap: { xs: 1.5, md: 2 }, gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }, mb: 2.25 }}>
            <Card tone="active" index={0} label="Active projects" value={k.active} sub={`${cities}${k.hasBudgets ? ` · ${fmtInr(k.revenue)} budget` : ''}`} />
            <Card tone="onSchedule" index={1} label="On schedule" value={k.onSchedule} sub={`${k.onSchedulePct}% on track`} />
            <Card tone="delayed" index={2} label="Delayed" value={k.delayed.length} sub={k.delayed.length ? 'needs attention' : 'all on track'} />
            <Card tone="completed" index={3} label="Completed" value={k.completed}
                sub={k.hasBudgets ? `${fmtInr(k.profit)} profit · ${k.margin}% margin` : `${k.notStarted} not started`} />
        </Box>
    );
}
